import { generatePath, NavLink } from 'react-router-dom';
import {
  formatBalance,
  formatDate,
  IconSelectedCircle,
  ButtonSecondary,
  ButtonSize,
  IconSend,
  IconExpand,
  IconBlock,
  Table,
  Pagination,
  IconRight,
  IconAttention,
  IconLoader,
  Currency,
  IconTrash,
  PermissionEnum,
  IconDownload,
} from '@ui/src';
import { Fintrac, FintracTransaction } from '../types';
import { expandableColumns } from '../variables/eftr';
import formatFintracTransactions from './format-fintrac-transactions';

import ROUTES from '../../../routes';

import cn from '../styles.module.css';

const formatFintrac = ({
  list,
  fintracTransactions,
  currencies,
  transactionsPagination,
  onRowsSelect,
  selectedTransactions,
  setCancelReportVisible,
  setSubmitReportVisible,
  setAddTransactionsSidepanelVisible,
  type,
  transactionsIsLoading,
  onToggleAll,
  deselectAllInProgress,
  selectAllInProgress,
  requestInProgress,
  selectedTransactionId,
  cadCurrency,
  setSelectedReportTransferDate,
  onDeleteClick,
  activePermissions,
  setSelectedReportAmount,
  onCheckStatus,
  reportIsLoading,
  reportDownloadInProgress,
  onDownloadReport,
}: {
  list: Fintrac[];
  fintracTransactions?: FintracTransaction[];
  currencies?: Currency[];
  transactionsPagination?: Pagination;
  onRowsSelect: (ids: string[], id?: string) => void;
  selectedTransactions?: string[];
  setCancelReportVisible: (data: boolean) => void;
  setSubmitReportVisible: (data: boolean) => void;
  setSelectedReportTransferDate: (data: string) => void;
  setAddTransactionsSidepanelVisible: (data: boolean) => void;
  type: 'eftr' | 'lvctr';
  transactionsIsLoading?: boolean;
  onToggleAll: (data: boolean) => void;
  deselectAllInProgress?: boolean;
  selectAllInProgress?: boolean;
  requestInProgress?: boolean;
  selectedTransactionId?: string;
  cadCurrency?: Currency;
  onDeleteClick: (id: string) => void;
  activePermissions?: PermissionEnum[];
  setSelectedReportAmount: (data: string) => void;
  onCheckStatus: () => void;
  reportIsLoading?: boolean;
  reportDownloadInProgress?: boolean;
  onDownloadReport: () => void;
}): Fintrac[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      aggregationType: (
        <div>
          {result.aggregationType
            .split('_')
            ?.map(
              (aggregationType) =>
                `${aggregationType.charAt(0)}${aggregationType.slice(1).toLowerCase()}`,
            )
            .join(' ')}
        </div>
      ),
      totalAmount: (
        <div>
          {result?.totalAmount
            ? formatBalance(result?.totalAmount, cadCurrency)
            : '0'}
        </div>
      ),
      status: (
        <div>
          {`${result.status.charAt(0)}${result.status.slice(1).toLowerCase()}`}{' '}
        </div>
      ),
      periodStart: (
        <div>{result?.periodStart ? formatDate(result.periodStart) : '-'}</div>
      ),
      deadlineAt: (
        <div className={cn.deadlineRow}>
          <div className={cn.deadlineRowValue}>
            {result?.deadlineAt ? formatDate(result.deadlineAt) : '-'}
          </div>
          {activePermissions?.includes(
            PermissionEnum.REGULATORY_REPORT_FINTRAC_DELETE_ACTION,
          ) &&
            (result?.status === 'CREATED' || result.status === 'CANCELLED') && (
              <button
                type="button"
                className={cn.deleteReportButton}
                onClick={() => onDeleteClick(result.id)}
              >
                <IconTrash />
              </button>
            )}
        </div>
      ),
      values: (
        <div className={cn.transactionsTableWrapper}>
          {transactionsIsLoading && (
            <div className={cn.transactionsLoader}>
              <IconLoader />
            </div>
          )}
          {!transactionsIsLoading && !fintracTransactions?.length && (
            <div className={cn.emptyTransactions}>
              <div className={cn.emptyTransactionsTitle}>
                Add transactions totalling at least 10,000 CAD
              </div>
              {result.status === 'CREATED' && (
                <div className={cn.emptyTransactionsButtonWrapper}>
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    text="Add transaction"
                    icon={<IconExpand className={cn.addReportIcon} />}
                    className={`${cn.reportButton} button-white ${cn.withMargin}`}
                    onClick={() => {
                      setAddTransactionsSidepanelVisible(true);
                      setSelectedReportTransferDate(result.periodStart);
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {!transactionsIsLoading &&
            fintracTransactions &&
            fintracTransactions?.length > 0 && (
              <Table
                columns={expandableColumns}
                data={
                  formatFintracTransactions(
                    fintracTransactions,
                    currencies,
                    cadCurrency,
                  ) || []
                }
                onSort={() => {}}
                filters={{}}
                sort={{}}
                columnsLabels={{}}
                onFilterChange={() => {}}
                take="15"
                setPage={() => {}}
                onRowsSelect={(ids, id) => {
                  onRowsSelect(ids, id);
                }}
                tableFormClassName={`${cn.transactionsTable} ${transactionsPagination && !transactionsPagination?.hasNextPage ? cn.withPadding : ''}`}
                tableTopClassName={cn.transactionsTableTop}
                rowsSelectDisabled={
                  (result?.status !== 'CREATED' &&
                    result?.status !== 'REJECTED') ||
                  deselectAllInProgress ||
                  selectAllInProgress
                }
                disabledRowIds={
                  requestInProgress && selectedTransactionId
                    ? [selectedTransactionId]
                    : []
                }
                selectedIds={selectedTransactions}
                onToggleAll={(toggle) => onToggleAll(toggle)}
                topContent={
                  <>
                    <div className={cn.transactionsTop}>
                      <div className={cn.transactionsTopLeft}>
                        <div className={cn.transactionsTopLeftContent}>
                          {result?.status === 'CREATED' &&
                            (result.totalAmount > 10000 ? (
                              <IconSelectedCircle width={20} height={20} />
                            ) : (
                              <IconAttention />
                            ))}
                          <div className={cn.totalAmount}>
                            {formatBalance(result.totalAmount)} CAD
                          </div>
                          &nbsp;
                          <div className={cn.allowedAmount}>/ 10 000 CAD</div>
                        </div>
                      </div>
                      {(result?.status === 'CREATED' ||
                        result.status === 'REJECTED' ||
                        result.status === 'SENT' ||
                        result.status === 'SUCCESS') && (
                        <div className={cn.transactionsTopRight}>
                          {(result?.status === 'SENT' ||
                            result?.status === 'SUCCESS') && (
                            <ButtonSecondary
                              className={`${cn.reportButton} ${cn.downloadButton}`}
                              onClick={onDownloadReport}
                              text={
                                reportDownloadInProgress
                                  ? 'Loading...'
                                  : 'Download'
                              }
                              icon={<IconDownload />}
                              size={ButtonSize.SMALL}
                            />
                          )}
                          {result?.status === 'SENT' && (
                            <ButtonSecondary
                              className={`button-white ${cn.reportButton} ${cn.checkStatusButton}`}
                              text={
                                reportIsLoading ? 'Loading...' : 'Check status'
                              }
                              size={ButtonSize.SMALL}
                              onClick={onCheckStatus}
                            />
                          )}
                          {result?.status !== 'SUCCESS' &&
                            result?.status !== 'SENT' && (
                              <>
                                <ButtonSecondary
                                  size={ButtonSize.SMALL}
                                  text="Submit report"
                                  icon={<IconSend />}
                                  className={`${cn.reportButton}  ${cn.withMargin}`}
                                  onClick={() => {
                                    setSubmitReportVisible(true);
                                    setSelectedReportAmount(
                                      formatBalance(
                                        result.totalAmount,
                                        cadCurrency,
                                      ),
                                    );
                                  }}
                                  disabled={result.totalAmount < 10000}
                                />
                                {result?.status !== 'REJECTED' && (
                                  <ButtonSecondary
                                    size={ButtonSize.SMALL}
                                    text="Add transaction"
                                    icon={
                                      <IconExpand
                                        className={cn.addReportIcon}
                                      />
                                    }
                                    className={`${cn.reportButton} button-white ${cn.withMargin}`}
                                    onClick={() => {
                                      setAddTransactionsSidepanelVisible(true);
                                      setSelectedReportTransferDate(
                                        result.periodStart,
                                      );
                                    }}
                                  />
                                )}
                                <ButtonSecondary
                                  size={ButtonSize.SMALL}
                                  text="Cancel report"
                                  icon={
                                    <IconBlock
                                      className={cn.cancelReportIcon}
                                    />
                                  }
                                  className={`${cn.reportButton} button-white ${cn.withMargin}`}
                                  onClick={() => setCancelReportVisible(true)}
                                />
                              </>
                            )}
                        </div>
                      )}
                    </div>
                    {result.totalAmount < 10000 &&
                      result.status === 'CREATED' && (
                        <div className={cn.amountError}>
                          The total value of the transactions must be at least
                          10,000 CAD
                        </div>
                      )}
                  </>
                }
                nestedTable
                withToggleInput
              />
            )}

          {transactionsPagination && transactionsPagination?.hasNextPage && (
            <div className={cn.moreTransactionsLinkWrapper}>
              <NavLink
                to={generatePath(ROUTES.FINTRAC_DETAILS, {
                  reportId: result.id,
                })}
                className={cn.moreTransactionsLink}
                state={{ type }}
              >
                Show all transactions
                <IconRight />
              </NavLink>
            </div>
          )}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

export default formatFintrac;
